import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import slugify from 'slugify';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEyeSlash, faPlus } from '@fortawesome/pro-regular-svg-icons';
import _ from 'lodash';
import cn from 'classnames';
import './ShopControls.scss';

import { openCuratorGroupModal } from '../../Actions/UIActions';
import { addCollection } from '../../Actions/StoreActions';

import { getPrettyNumber, getFormattedSectionTitle } from '../../Helpers/formatting';
import { getGMTTime } from '../../Helpers/formatting';
import { getSections, isShopper, getUserId } from '../../Helpers/user_helpers';
import { removeUrlParam } from '../../Helpers/helpers';
import { spotlightKeys } from '../../Helpers/spotlight_helpers';

import ShopSectionsModal from '../Sections/ShopSectionsModal';
import ShopAddPostSelectType from './Elements/ShopAddPostSelectType';
import SearchMediaModal from '../Modals/SearchMediaModal';
import Spotlight from '../Spotlight/Spotlight';
import FacebookSDK from '../General/FacebookSDK';
import Loader from '../Loader/Loader';

const ShopControls = props => {
  const { user, isShowingCollections, isYourShop } = props;

  const [showingAllFilters, setShowingAllFilters] = React.useState(false);
  const showAllFilters = () => setShowingAllFilters(true);
  const showLessFilters = () => setShowingAllFilters(false);

  const activeFilters = isShowingCollections
    ? []
    : props.activeProductRefinements
        .filter(i => !i.label.includes('Curated By'))
        .map(item => ({
          label: item.displayValue,
          sublabel: item.label,
          active: true,
          isAdjusting: props.isAdjustingLabel(item.label),
          onClick: () => {
            props.applyUnrefinement(item);
            window.__ADD_EVENT__('Shop - Click Unrefinement', { label: item.label });
          }
        }));

  const isSectionActive = section => {
    return props.Section_id
      ? props.Section_id === section.id
      : props.Section_title
      ? props.Section_title === slugify(section.title).toLowerCase()
      : false;
  };

  const potentialFilters = isShowingCollections
    ? isYourShop
      ? isShopper(user)
        ? []
        : getSections(user).map(section => ({
            label: getFormattedSectionTitle(section),
            active: isSectionActive(section),
            disableClose: true,
            icon: section.isHidden ? faEyeSlash : null,
            onClick: () => props.goToSectionId(section.id)
          }))
      : props.sectionResults.length === 1
      ? []
      : props.Curator_id || props.Curator_username
      ? props.sectionResults.map((section, idx) => ({
          label: getFormattedSectionTitle(section),
          active: isSectionActive(section),
          disableClose: true,
          onClick: () => {
            props.goToSectionId(section.id);
            window.__ADD_EVENT__('Shop - Click Shop Section', { title: section.title });
          }
        }))
      : props.sectionResults.map((section, idx) => {
          return {
            label: section.label,
            active: isSectionActive(section),
            disableClose: true,
            onClick: () => {
              props.goToSectionType(section.sectionType);
              window.__ADD_EVENT__('Shop - Click Group Shop Section', { type: section.sectionType });
            }
          };
        })
    : _.flatten(
        props.productRefinements.map(refinement =>
          refinement.items.map(item => ({
            label: item.label,
            count: item.count,
            isAdjusting: props.isAdjustingLabel(item.label),
            onClick: () => {
              props.applyRefinement(item, refinement);
              window.__ADD_EVENT__('Shop - Click Refinement', { label: item.label, count: item.count });
            }
          }))
        )
      );

  const filters = _.filter(_.uniqBy([...activeFilters, ...potentialFilters], 'label'), f => f.label);
  const hasSearchQuery = !!props.query;

  const clearSearchQuery = () => {
    props.setQuery('');
    window.__ADD_EVENT__('Shop - Click Clear Search Query');

    // Remove from query params
    removeUrlParam('query');
  };

  const filterNamesHash = filters.map(f => f.label).join(',');
  React.useEffect(() => {
    showingAllFilters && setShowingAllFilters(false);
  }, [filterNamesHash]);

  // Show the "Show More" button if it overlaps with the filters
  const showMoreRef = React.useRef();
  const lastFilterRef = React.useRef();
  React.useEffect(() => {
    if (showMoreRef.current && lastFilterRef.current) {
      const filtersRect = lastFilterRef.current.getBoundingClientRect();
      const showMoreRect = showMoreRef.current.getBoundingClientRect();
      if (filtersRect.x + filtersRect.width > showMoreRect.x) {
        showMoreRef.current.style.opacity = 1;
      } else {
        showMoreRef.current.style.opacity = 0;
      }
    }
  }, [filterNamesHash, lastFilterRef]);

  // Sections
  const [isSectionsModalVisible, setIsSectionsModalVisible] = React.useState(false);
  const [shopSectionsModalInitialConfig, setShopSectionsModalInitialConfig] = React.useState({});
  const closeSectionsModal = () => setIsSectionsModalVisible(false);
  const openSectionsModal = () => setIsSectionsModalVisible(true);

  // Collections
  const [isSelectTypeModalVisible, setIsSelectTypeModalVisible] = React.useState(false);

  // Posts
  const isPostCollection = props.activeSection?.previewType?.includes('post');
  const closeSelectTypeModal = () => setIsSelectTypeModalVisible(false);
  const openSelectTypeModal = () => setIsSelectTypeModalVisible(true);
  const [isSearchingMediaType, setIsSearchingMediaType] = React.useState(null);
  const closeSearchMediaModal = () => setIsSearchingMediaType(null);
  const selectMediaType = type => setIsSearchingMediaType(type);

  const [isAddingCollection, setIsAddingCollection] = React.useState(false);
  const addManualCollection = () => addCollection({ forceManual: true });
  const addCollection = async (options = {}) => {
    const { forceManual = false } = options;
    if (isPostCollection && !forceManual) {
      openSelectTypeModal();
    } else {
      setIsAddingCollection(true);
      const isArchived = !!props.showArchivedCollections;
      const addToTop = true;
      const newCollection = {
        private: false,
        User_id: getUserId(props.user),
        Section_id: props.activeSection?.id || null,
        isArchived,
        sortOrderRank: addToTop ? -1 * getGMTTime() : getGMTTime()
      };
      const resp = await props.addCollection(newCollection);
      resp.collection ? props.history.push(`/collections/${resp.collection.id}`) : setIsAddingCollection(false);
    }
  };
  const addMediaCollection = async media => {
    const { type, url, createdAt, image, title } = media;

    let name;
    if (type === 'tiktok') name = `Post on ${moment(createdAt).format('MMMM Do')}`;
    else if (type === 'instagram') name = `Post on ${moment(createdAt).format('MMMM Do')}`;
    else if (type === 'youtube') name = title;
    else name = title;

    const newCollection = {
      name,
      User_id: getUserId(props.user),
      Section_id: props.activeSection?.id || null,
      image,
      social_links: url,
      sortOrderRank: -1 * getGMTTime() // Add to top of collections
    };

    const resp = await props.addCollection(newCollection);
    resp.collection ? props.history.push(`/collections/${resp.collection.id}`) : setIsAddingCollection(false);
  };

  // UI Handling
  const showFiltersBar = !!filters.length || props.isFetchingProductRefinements || isYourShop;
  const showArchiveFilter = isShowingCollections && props.isYourShop && !isShopper(props.user);
  const showAddCollectionBtn = props.isYourShop && props.isShowingCollections && !isShopper(props.user);
  const additionalClasses = { adjusting: props.isFetchingResults, hidden: !showFiltersBar };

  // Dynamic UI Handling
  const [leftAlignFilters, setLeftAlignFilters] = React.useState(false);
  const filterDisplayHash = filters.map(f => f.label).join(',');
  React.useEffect(() => {
    // Check the position the filters to see if we need it to be left aligned
    const filters = document.querySelectorAll('.shop-controls-container .filter');
    const firstFilter = filters[0];
    const lastFilter = filters[filters.length - 1];
    if (firstFilter) {
      const firstFilterRect = firstFilter.getBoundingClientRect();
      const isFirstFilterOffScreen = firstFilterRect.x < 20;
      const lastFilterRect = lastFilter.getBoundingClientRect();
      const isLastFilterOffScreen = lastFilterRect.x > window.innerWidth - 20;
      if (isFirstFilterOffScreen && !leftAlignFilters) {
        setLeftAlignFilters(true);
      } else if (!isLastFilterOffScreen && leftAlignFilters) {
        setLeftAlignFilters(false);
      }
    }
  }, [filterDisplayHash, props.tab]);

  return (
    <>
      <div className={cn('shop-controls-container', additionalClasses)}>
        {showAddCollectionBtn && <FacebookSDK />}
        {showFiltersBar && (
          <div className={cn('filters', { 'showing-all': showingAllFilters, 'left-align': leftAlignFilters })}>
            {hasSearchQuery && (
              <div className='filter active' onClick={clearSearchQuery}>
                <div className='label'>{props.query}</div>
                <div className='remove-icn'>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </div>
            )}
            {filters.map((filter, index) => {
              const { label, count, active, isAdjusting, icon, onClick, disableClose } = filter;
              return (
                <div
                  ref={index === filters.length - 1 ? lastFilterRef : null}
                  className={cn('filter', { active, inactive: !active, adjusting: isAdjusting })}
                  onClick={onClick}
                  key={label + index}
                >
                  <div className='label'>{label}</div>
                  {active && !disableClose ? (
                    <div className='remove-icn'>
                      <FontAwesomeIcon icon={faTimes} />
                    </div>
                  ) : (
                    count && <div className='count'>{getPrettyNumber(count)}</div>
                  )}
                  {icon && (
                    <div className='icon'>
                      <FontAwesomeIcon icon={icon} />
                    </div>
                  )}
                </div>
              );
            })}
            {isYourShop && isShowingCollections && !isShopper(user) && (
              <div className={cn('filter', { active: props.showArchivedCollections })} onClick={props.goToArchivedCollections}>
                <div className='label'>Archived</div>
              </div>
            )}
            {showArchiveFilter && (
              <div className='filter edit' onClick={openSectionsModal}>
                <div>Edit Sections</div>
              </div>
            )}
            {showingAllFilters && (
              <div onClick={showLessFilters} className='filter show-less-filter'>
                <div className='label'>Show Less</div>
              </div>
            )}
            {!showingAllFilters && (
              <div ref={showMoreRef} className='show-all-btn-container'>
                <div onClick={showAllFilters} className='show-all-btn'>
                  Show All
                </div>
              </div>
            )}
            {showAddCollectionBtn && (
              <div className='add-collection-btn-container'>
                <Spotlight spotlightKey={isPostCollection ? spotlightKeys.CREATE_POST : spotlightKeys.CREATE_SHELF}>
                  <div onClick={addCollection} className={cn('add-collection-btn', { adding: isAddingCollection })}>
                    <div className='label'>Add {isPostCollection ? 'Post' : 'Collection'}</div>
                    <div className='icon'>
                      <FontAwesomeIcon icon={faPlus} />
                    </div>
                    {isAddingCollection && <Loader size={48} />}
                  </div>
                </Spotlight>
              </div>
            )}
          </div>
        )}
      </div>
      {showAddCollectionBtn && (
        <div className='add-collection-mobile-container'>
          <div onClick={addCollection} className='add-collection-mobile-btn'>
            <div className='icon'>
              <FontAwesomeIcon icon={faPlus} />
            </div>
            <div className='text'>
              {isAddingCollection ? (
                'Adding...'
              ) : props.showArchivedCollections ? (
                <div className='text'>
                  Add Collection to <span className='section-title'>Archive</span>
                </div>
              ) : props.activeSection ? (
                <>
                  Add Collection to <span className='section-title'>{getFormattedSectionTitle(props.activeSection)}</span>
                </>
              ) : (
                'Add Collection'
              )}
            </div>
          </div>
        </div>
      )}
      {isSectionsModalVisible && (
        <ShopSectionsModal close={closeSectionsModal} sectionsUser={props.user} initConfig={shopSectionsModalInitialConfig} />
      )}
      {isSelectTypeModalVisible && (
        <ShopAddPostSelectType
          closeModal={closeSelectTypeModal}
          selectMediaType={selectMediaType}
          addManualCollection={addManualCollection}
          isAddingManualCollection={isAddingCollection}
        />
      )}
      {isSearchingMediaType && (
        <SearchMediaModal
          type={isSearchingMediaType}
          Searching_User_id={getUserId(props.user)}
          selectResult={addMediaCollection}
          addCollection={addCollection}
          closeModal={closeSearchMediaModal}
        />
      )}
    </>
  );
};

ShopControls.propTypes = {
  user: PropTypes.object.isRequired,

  // Hierarchy Based
  Category_id: PropTypes.number,
  Department_id: PropTypes.number,
  Industry_id: PropTypes.number,
  AllBrand_id: PropTypes.number,
  shoppingHeaderSentence: PropTypes.string,

  // Tab Based
  tab: PropTypes.string,
  goToTab: PropTypes.func.isRequired,
  isForYouSection: PropTypes.bool.isRequired,

  // Search Based
  query: PropTypes.string,
  setQuery: PropTypes.func.isRequired,

  // Curators Based
  Curator_id: PropTypes.number,
  CuratorGroup_id: PropTypes.number,

  // Loading
  isFetchingProductRefinements: PropTypes.bool.isRequired,
  isFetchingResults: PropTypes.bool.isRequired,

  // Refinements
  productRefinements: PropTypes.array.isRequired,
  activeRefinementObjects: PropTypes.object.isRequired,
  activeProductRefinements: PropTypes.array.isRequired,
  applyRefinement: PropTypes.func.isRequired,
  applyUnrefinement: PropTypes.func.isRequired,
  isAdjustingLabel: PropTypes.func.isRequired,

  // Collections
  isShowingCollections: PropTypes.bool.isRequired,
  goToSectionId: PropTypes.func.isRequired,
  goToSectionType: PropTypes.func.isRequired,
  sectionType: PropTypes.string,

  // Actions
  openCuratorGroupModal: PropTypes.func.isRequired
};
const mapStateToProps = state => {
  const { user } = state;
  return { user };
};

export default connect(mapStateToProps, {
  openCuratorGroupModal,
  addCollection
})(withRouter(ShopControls));
